import { Divider, Grid, MenuItem, TextField, Typography, styled } from '@mui/material';
import CurrencyInput from 'components/CurrencyInput';
import PhoneNumberInput from 'components/PhoneNumberInput';
import SelectInput from 'components/SelectInput';
import TextInput from 'components/TextInput';
import { SectionStyled } from 'containers/FormStep1Container';
import { lazy, Suspense, ReactNode, Fragment, useEffect } from 'react';
import { FormBuilderField } from 'types/form.type';
import { Controller, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form';
import { t } from 'i18next';
import { useSearchParams } from 'react-router-dom';
import AutocompleteInput from 'components/AutocompleteInput';
import RadioInput from 'components/RadioInput';

const AsyncFileUploader = lazy(() => import('containers/AsyncFileUploader'));

interface CustomFieldsProps {
  field: FormBuilderField;
  hookForm: ControllerRenderProps<FieldValues, string>;
  isConditional?: boolean;
}

const SubtitleStyled = styled('li')(({ theme }) => ({
  fontWeight: 400,
  fontSize: 12,
  color: theme.palette.surface[700],
  '&:last-of-type': {
    marginBottom: 16
  }
}));

const CustomFields = ({ field, hookForm, isConditional }: CustomFieldsProps) => {
  if (field.type === 'title') {
    return (
      <Typography color="surface.700" variant="h7" component="p" sx={{ mb: 2 }}>
        {field.label}
      </Typography>
    );
  }

  if (field.type === 'subtitle') {
    return <SubtitleStyled>{field.label}</SubtitleStyled>;
  }

  if (field.type === 'divider') return <Divider sx={{ width: '100%', my: 2 }} />;

  if (field.type === 'text' || field.type === 'number')
    return (
      <TextInput
        isConditional={isConditional}
        label={field.label}
        type={field.type}
        placeholder={field.placeholder}
        fullWidth={field.fullWidth}
        hookForm={hookForm}
        disabled={field.disabled}
        sx={{ minWidth: field.fullWidth ? 'unset' : 308 }}
      />
    );

  if (field.type === 'select') {
    return (
      <SelectInput
        name={field.name}
        label={field.label}
        defaultValue={field.defaultValue}
        fullWidth={field.fullWidth}
        placeholder={field.placeholder}
        hookForm={hookForm}
        sx={{ minWidth: field.fullWidth ? 'unset' : 308, mt: 3 }}
      >
        {field.options?.map((option, index) => (
          <MenuItem key={option.value || index} value={option.value}>
            {t(option.label)}
          </MenuItem>
        ))}
      </SelectInput>
    );
  }

  if (field.type === 'autocomplete') {
    return (
      <AutocompleteInput
        name={field.name}
        label={field.label}
        fullWidth={field.fullWidth}
        placeholder={field.placeholder}
        options={field.options || []}
        renderInput={(params) => <TextField {...params} />}
        hookForm={hookForm}
      />
    );
  }

  if (field.type === 'phonenumber')
    return (
      <PhoneNumberInput
        name={field.name}
        label={field.label}
        placeholder={field.placeholder}
        fullWidth={field.fullWidth}
        hookForm={hookForm}
        inputRef={hookForm.ref}
        sx={{ minWidth: field.fullWidth ? 'unset' : 202 }}
      />
    );

  if (field.type === 'currency') {
    return (
      <CurrencyInput
        name={field.name}
        label={field.label}
        placeholder={field.placeholder}
        fullWidth={field.fullWidth}
        hookForm={hookForm}
        sx={{ minWidth: field.fullWidth ? 'unset' : 264 }}
      />
    );
  }

  if (field.type === 'radio')
    return (
      <RadioInput
        name={field.name}
        label={field.label}
        fullWidth={field.fullWidth}
        options={field.options ?? []}
        hookForm={hookForm}
      />
    );

  if (field.type === 'file')
    return (
      <Suspense fallback={<></>}>
        <AsyncFileUploader
          name={field.name}
          label={field.label}
          allowMultiple={field.allowMultiple}
          sx={{ width: field.fullWidth ? '100%' : 'unset' }}
          hookForm={hookForm}
        />
      </Suspense>
    );

  return <> </>;
};

interface FormBuilderProps {
  formFields: FormBuilderField[];
  headerElement?: ReactNode;
  footerElement?: ReactNode;
}

export default function FormBuilder({ formFields, headerElement, footerElement }: FormBuilderProps) {
  const [searchParams] = useSearchParams();
  const step = searchParams.get('step') || '1';
  const section = searchParams.get('section') || '1';
  const { control, trigger, watch } = useFormContext();

  useEffect(() => {
    if (step === '2' && section === '1') trigger('country');
  }, [section, step, trigger]);

  return (
    <SectionStyled sx={{ display: 'flex', flexDirection: 'column', alignItems: 'baseline' }}>
      {headerElement}
      {formFields.map((field, index) => {
        let isConditional: boolean | undefined;

        if (Array.isArray(field.conditional?.value)) {
          isConditional = field.conditional && !field.conditional.value.includes(watch(field.conditional.dependOn));
        } else {
          isConditional = field.conditional && watch(field.conditional.dependOn) !== field.conditional.value;
        }

        if (isConditional) {
          return null;
        }

        if (field.type === 'group') {
          return (
            <Fragment key={field.label}>
              <Typography color="surface.700" variant="h7" component="p" sx={{ mb: 1 }}>
                {field.label}
              </Typography>
              <SectionStyled key={field.name} sx={{ backgroundColor: 'surface.100' }}>
                <Grid container columnSpacing={2} columns={16}>
                  {field.children?.map((child, index) => (
                    <Grid item xs={child.span} key={child.label || index}>
                      <Controller
                        control={control}
                        name={child.name || ''}
                        rules={child.validation}
                        render={({ field: hookForm }) => <CustomFields field={child} hookForm={hookForm} />}
                      />
                    </Grid>
                  ))}
                </Grid>
              </SectionStyled>
            </Fragment>
          );
        }

        return (
          <Controller
            key={`${field.label || field.name}-${field.type}` || index}
            control={control}
            name={field.name || ''}
            rules={field.validation}
            render={({ field: hookForm }) => (
              <CustomFields field={field} hookForm={hookForm} isConditional={isConditional === false} />
            )}
          />
        );
      })}
      {footerElement}
    </SectionStyled>
  );
}
